import { cartierDebug } from 'Utils/Debug'
import { debounce } from 'Utils/Utility'

/**
 * This utility class add classes to HTML tag based on device informations and device orientation
 *
 * @class Devices
 */
class Devices {
	static get breakpoints() {
		return {
			'smartphone-portrait': '(max-width: 567px)',
			landscape: '(orientation: landscape)',
			smartphone: '(max-width: 767px)',
			'tablet-portrait': '(min-width: 768px)',
			'tablet-portrait-upper': '(min-width: 769px)',
			'tablet-landscape': '(min-width: 1024px)',
			mobile: '(max-width: 1024px)',
			desktop: '(min-width: 1025px)',
			'desktop-upper': '(max-width: 1279px)',
			'desktop-medium': '(min-width: 1280px)',
			'desktop-large': '(min-width: 1920px)',
			'desktop-maxWidth': '(min-width: 1921px)',
		}
	}

	/**
	 * Creates an instance of Devices.
	 * Get useragent data, orientation information and set classes
	 * @memberof Devices
	 */
	constructor() {
		this.media = {}
		this.prefersReduceMotionMatch = this.prefersReduceMotionMatch.bind(this)

		this.setMediaReduceMotion()
		this.setMediaBreakpoints()
		this.setIsSafariCssClass()

		this.notifyGeneralMediaChange = debounce(() => yTos.notify('CAR::mediaChanged', this.media))
	}

	setMediaReduceMotion() {
		const mediaPrefersReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
		this.prefersReduceMotion = mediaPrefersReduceMotion.matches

		try {
			// Chrome & Firefox
			mediaPrefersReduceMotion.addEventListener('change', this.prefersReduceMotionMatch)
		} catch (e1) {
			try {
				// Safari
				mediaPrefersReduceMotion.addListener(this.prefersReduceMotionMatch)
			} catch (e2) {
				cartierDebug('error', e1)
			}
		}
	}

	setMediaBreakpoints() {
		Object.keys(Devices.breakpoints).forEach((breakpoint) => {
			this.media[breakpoint] = window.matchMedia(Devices.breakpoints[breakpoint])
			this.media[breakpoint].addListener(() => {
				if (breakpoint === 'landscape') {
					this.handleOrientationChange(this.media[breakpoint])
				}

				this.notifyMediaChange(this.media[breakpoint], breakpoint)
				this.notifyGeneralMediaChange()
			})
		})
	}

	setIsSafariCssClass() {
		// Detect Safari in order to fix a bug
		// https://stackoverflow.com/questions/7944460/detect-safari-browser
		const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
		if (isSafari) {
			document.documentElement.classList.add('safari')
		}
	}

	/**
	 * Change classes when orientation change
	 * @param {Object} media - media query obj data
	 * @param {bool} media.matches - The matching value of the media query
	 * @memberof Devices
	 */
	handleOrientationChange(media) {
		if (media.matches) {
			document.documentElement.classList.remove('portrait')
			document.documentElement.classList.add('landscape')
		} else {
			document.documentElement.classList.remove('landscape')
			document.documentElement.classList.add('portrait')
		}
	}

	/**
	 * Handle prefers-reduce-motion Change
	 * - change --duration css variable
	 * - set this.prefersReduceMotion boolean on the class.
	 *
	 * @param {Object} media - media query obj data
	 * @memberof Devices
	 */
	prefersReduceMotionMatch(media) {
		this.prefersReduceMotion = media.matches
	}

	/**
	 * Fire events only if media matches
	 *
	 * @param {Object} media - media query obj data
	 * @param {String} scope
	 * @memberof Devices
	 */
	notifyMediaChange(media, scope) {
		if (media.matches) {
			yTos.notify('CAR::mediaChanged::breakpoint', media, { scope })
		}
	}
}

export default Devices
